import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getAgentMetadataHelpers } from '@/entities/agent';
import {
  useChatStoreApi,
  useCurrentAgent,
  useIsSuggestionsLoading,
  useMessages,
  useSuggestions,
} from '@/features/chats/stores';
import type { UseChatSuggestionsProps } from './useChatSuggestions.types';

export const useChatSuggestions = ({ onSend }: UseChatSuggestionsProps) => {
  const chatStore = useChatStoreApi();
  const agent = useCurrentAgent();
  const messages = useMessages();
  const suggestions = useSuggestions();
  const isSuggestionsLoading = useIsSuggestionsLoading();
  const hasTriggeredInitialFetch = useRef(false);

  // Get agent-specific hardcoded suggestions
  const agentSuggestions = useMemo(
    () => (agent?.id ? getAgentMetadataHelpers().getAgentSuggestions(agent.id) : []),
    [agent?.id],
  );

  // Determine which suggestions to show based on conversation state
  const hasConversation = messages.length > 0;

  // Reset fetch trigger when agent changes
  const prevAgentId = useRef<string | undefined>(undefined);

  // Fetch suggestions before conversation starts for agents with autoFetchSuggestions
  useEffect(() => {
    // Reset when agent changes
    if (prevAgentId.current !== agent?.id) {
      hasTriggeredInitialFetch.current = false;
      prevAgentId.current = agent?.id;
    }

    if (!agent?.id || hasTriggeredInitialFetch.current) return;

    const shouldAutoFetch = getAgentMetadataHelpers().getAgentAutoFetchSuggestions(agent.id);
    if (shouldAutoFetch && !hasConversation && !isSuggestionsLoading) {
      hasTriggeredInitialFetch.current = true;
      void chatStore.getState().fetchSuggestions();
    }
  }, [chatStore, agent?.id, hasConversation, isSuggestionsLoading]);

  const suggestionsToShow = useMemo(() => {
    // If we have fetched suggestions, show them (indicates an ongoing conversation)
    // This handles streaming mode where chat store messages are empty
    if (suggestions.length > 0) {
      return suggestions;
    }

    // If no conversation yet, show default agent suggestions (hardcoded suggestions)
    if (!hasConversation) {
      return agentSuggestions;
    }

    // Ongoing conversation but no suggestions fetched yet - return empty
    return [];
  }, [hasConversation, agentSuggestions, suggestions]);

  // Handle clicking on a suggestion
  const handleStarterClick = useCallback(
    (starter: string) => {
      if (!agent?.id) return;
      // Preserve the current chat-input options (e.g. the model selected in the
      // right-side chip, stored under `llmOptions.model`) so a suggestion click
      // doesn't fall back to the agent default. Mirrors handleSendMessageInternal:
      // knowledgeBaseIds is passed separately from the rest of the inputs.
      const { knowledgeBaseIds, ...inputs } = chatStore.getState().chatInput.options ?? {};
      void onSend({
        agentId: agent.id,
        prompt: starter,
        inputs,
        knowledgeBaseIds: knowledgeBaseIds as string[] | undefined,
        files: [],
      });
    },
    [chatStore, agent?.id, onSend],
  );

  return {
    isSuggestionsLoading,
    suggestionsToShow,
    handleStarterClick,
  };
};

/** Whether the chat's agent shows suggestions at all: starters, or ones fetched after replies. */
export const useAgentOffersSuggestions = () => {
  const agent = useCurrentAgent();
  if (!agent?.id || agent.suggestions === false) return false;
  const metadata = getAgentMetadataHelpers();
  return (
    metadata.getAgentAutoFetchSuggestions(agent.id) ||
    metadata.getAgentSuggestions(agent.id).length > 0
  );
};
